import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlug, FaSync, FaDownload, FaFlask, FaTimesCircle, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { colors, shadows } from '../../../employees/styles/personalakteStyles';
import LightspeedService, {
  LightspeedStatus,
  LightspeedBusiness,
  LightspeedBusinessLocation,
  LightspeedImportResult,
} from '../services/LightspeedService';

const LS_GREEN = '#00B388';

const ERROR_MESSAGES: Record<string, string> = {
  reauth_required: 'Verbindung abgelaufen – bitte neu verbinden.',
  forbidden_scope_or_access: 'Kein Zugriff – bitte Rechte/Scopes in Lightspeed prüfen.',
  rate_limited: 'Zu viele Anfragen – bitte später erneut versuchen.',
  lightspeed_down: 'Lightspeed technische Störung – bitte später erneut versuchen.',
};

function formatDE(dateStr: string): string {
  return new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function iso(d: Date): string {
  return d.toISOString();
}

export default function LightspeedIntegration(): React.JSX.Element {
  const navigate = useNavigate();

  const [status, setStatus] = useState<LightspeedStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [envChoice, setEnvChoice] = useState<'trial' | 'prod'>('prod');

  const [businesses, setBusinesses] = useState<LightspeedBusiness[]>([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<number | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<number | null>(null);
  const [savingMapping, setSavingMapping] = useState(false);

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<LightspeedImportResult | null>(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const pollRef = useRef<number | undefined>(undefined);

  const loadStatus = useCallback(async () => {
    try {
      const s = await LightspeedService.getStatus();
      setStatus(s);
    } catch (e: any) {
      const code = e?.response?.status ?? e?.status;
      if (code === 404) {
        setStatus({ connected: false });
      } else {
        setStatus({ connected: false });
        setError(e?.response?.data?.detail || 'Status konnte nicht geladen werden.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Light polling every 60s when connected
  useEffect(() => {
    if (status?.connected && !connecting) {
      pollRef.current = window.setInterval(loadStatus, 60_000);
    }
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = undefined;
      }
    };
  }, [status?.connected, connecting, loadStatus]);

  const handleConnect = async () => {
    setError('');
    setSuccess('');
    setConnecting(true);
    try {
      const { authorize_url } = await LightspeedService.startConnect(envChoice);
      window.open(authorize_url, '_blank', 'noopener');
    } catch (e: any) {
      setConnecting(false);
      setError(e?.response?.data?.detail || 'Verbindung konnte nicht gestartet werden.');
      return;
    }

    // Poll for connection status after redirect
    let elapsed = 0;
    pollRef.current = window.setInterval(async () => {
      elapsed += 3000;
      if (elapsed > 5 * 60 * 1000) {
        window.clearInterval(pollRef.current);
        pollRef.current = undefined;
        setConnecting(false);
        setError('Verbindungs-Timeout. Bitte versuchen Sie es erneut.');
        return;
      }
      try {
        const s = await LightspeedService.getStatus();
        if (s.connected) {
          window.clearInterval(pollRef.current);
          pollRef.current = undefined;
          setStatus(s);
          setConnecting(false);
          setSuccess('Lightspeed erfolgreich verbunden!');
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Lightspeed Verbindung wirklich trennen?')) return;
    setDisconnecting(true);
    setError('');
    setSuccess('');
    try {
      await LightspeedService.disconnect();
      setStatus({ connected: false });
      setBusinesses([]);
      setSelectedBusiness(null);
      setSelectedLocation(null);
      setImportResult(null);
      setSuccess('Verbindung getrennt.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Trennen fehlgeschlagen.');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleLoadBusinesses = async () => {
    setLoadingBusinesses(true);
    setError('');
    try {
      const list = await LightspeedService.getBusinesses();
      setBusinesses(Array.isArray(list) ? list : []);
      if (list.length === 0) {
        setError('Keine Businesses/Standorte in Lightspeed gefunden.');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Standorte konnten nicht geladen werden.');
    } finally {
      setLoadingBusinesses(false);
    }
  };

  const handleSaveMapping = async () => {
    if (!selectedBusiness || !selectedLocation) return;
    setSavingMapping(true);
    setError('');
    try {
      await LightspeedService.setDefaultMapping(selectedBusiness, selectedLocation);
      await loadStatus();
      setSuccess('Standard-Standort gespeichert.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Mapping konnte nicht gespeichert werden.');
    } finally {
      setSavingMapping(false);
    }
  };

  const handleImport = async (hours: number) => {
    const blId = selectedLocation || status?.default_business_location_id;
    if (!blId) {
      setError('Bitte zuerst einen Standort auswählen.');
      return;
    }
    setImporting(true);
    setImportResult(null);
    setError('');
    setSuccess('');
    try {
      const now = new Date();
      const from = hours === 48
        ? new Date(now.getTime() - 48 * 60 * 60 * 1000)
        : new Date(new Date(now).setHours(0, 0, 0, 0) - (6 * 24 * 60 * 60 * 1000));
      const result = await LightspeedService.importSales(blId, iso(from), iso(now));
      setImportResult(result);
      setSuccess(`Import erfolgreich: ${result.sales_count} Transaktionen`);
      loadStatus();
    } catch (e: any) {
      const data = e?.response?.data;
      const requestId = data?.request_id;
      let msg = data?.detail || 'Import fehlgeschlagen.';
      if (requestId) msg += ` (Fehler-ID: ${requestId})`;
      setError(msg);
    } finally {
      setImporting(false);
    }
  };

  const selectedBusinessObj = businesses.find(b => b.id === selectedBusiness);
  const availableLocations: LightspeedBusinessLocation[] = selectedBusinessObj?.locations ?? [];
  const hasMapping = !!(status?.default_business_location_id);
  const errorCode = status?.error_code;
  const errorMsg = errorCode ? (ERROR_MESSAGES[errorCode] || status?.error_detail || 'Unbekannter Fehler') : null;

  if (loading) {
    return (
      <div style={{ padding: 24, maxWidth: 900 }}>
        <div style={{ textAlign: 'center', padding: 40, color: colors.textMuted, fontSize: 13 }}>
          Lade Lightspeed Status...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      {/* Back */}
      <button
        onClick={() => navigate('/settings/integrations')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '8px 16px', background: 'transparent', border: `1px solid ${colors.border}`,
          borderRadius: 9999, cursor: 'pointer', fontSize: 13, color: colors.textMuted,
          marginBottom: 20,
        }}
      >
        <FaArrowLeft style={{ fontSize: 11 }} /> Zurück
      </button>

      {/* Title */}
      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.navy, margin: '0 0 6px' }}>
        Lightspeed (K-Series) – Umsatzimport
      </h1>
      <p style={{ fontSize: 13, color: colors.textMuted, margin: '0 0 24px' }}>
        Read-only: SundaeOS liest Umsatz- und Steuerdaten aus Lightspeed. Keine Änderungen in Lightspeed.
      </p>

      {/* Messages */}
      {error && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 16px', marginBottom: 16, borderRadius: 10,
          background: colors.dangerLight, border: `1px solid ${colors.danger}`,
          fontSize: 13, color: '#991b1b',
        }}>
          <FaTimesCircle style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 16px', marginBottom: 16, borderRadius: 10,
          background: colors.successLight, border: `1px solid ${colors.success}`,
          fontSize: 13, color: '#166534',
        }}>
          <FaCheckCircle style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{success}</span>
        </div>
      )}

      {/* Connection Error Banner */}
      {status?.connected && errorMsg && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 16px', marginBottom: 16, borderRadius: 10,
          background: errorCode === 'reauth_required' || errorCode === 'forbidden_scope_or_access'
            ? colors.dangerLight : colors.warningLight,
          border: `1px solid ${errorCode === 'reauth_required' || errorCode === 'forbidden_scope_or_access'
            ? colors.danger : colors.warning}`,
          fontSize: 13,
          color: errorCode === 'reauth_required' || errorCode === 'forbidden_scope_or_access'
            ? '#991b1b' : '#92400e',
        }}>
          <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div>{errorMsg}</div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              {errorCode === 'reauth_required' && (
                <button onClick={handleConnect} style={btnSmall(LS_GREEN)}>Neu verbinden</button>
              )}
              <button onClick={loadStatus} style={btnSmall(colors.slate)}>Erneut prüfen</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Section 1: Connection ── */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: LS_GREEN,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FaPlug style={{ color: '#fff', fontSize: 16 }} />
            </div>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: colors.navy, margin: 0 }}>Verbindung</h2>
          </div>
          <StatusBadge status={status} connecting={connecting} />
        </div>

        {!status?.connected && !connecting && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <select
              value={envChoice}
              onChange={e => setEnvChoice(e.target.value as 'trial' | 'prod')}
              style={{
                padding: '8px 12px', borderRadius: 8, border: `1px solid ${colors.border}`,
                fontSize: 13, outline: 'none', background: colors.bgCard,
              }}
            >
              <option value="prod">Produktion</option>
              <option value="trial">Trial / Sandbox</option>
            </select>
            <button onClick={handleConnect} style={btnPrimary(LS_GREEN)}>
              <FaPlug style={{ fontSize: 12 }} /> Mit Lightspeed verbinden
            </button>
          </div>
        )}

        {connecting && (
          <div style={{
            padding: '12px 16px', borderRadius: 10,
            background: '#e0f7f1', border: `1px solid ${LS_GREEN}`,
            fontSize: 13, color: '#065f46',
          }}>
            Bitte im Lightspeed-Fenster anmelden und Zugriff erlauben...
          </div>
        )}

        {status?.connected && !errorCode && (
          <>
            <div style={{
              display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px',
              fontSize: 13, color: colors.textBody, marginBottom: 16,
            }}>
              <span style={{ color: colors.textMuted }}>Umgebung:</span>
              <span style={{ fontWeight: 600 }}>{status.env === 'trial' ? 'Trial / Sandbox' : 'Produktion'}</span>

              {status.expires_at && (
                <>
                  <span style={{ color: colors.textMuted }}>Token läuft ab:</span>
                  <span>{formatDE(status.expires_at)}</span>
                </>
              )}
              {status.last_sync_at && (
                <>
                  <span style={{ color: colors.textMuted }}>Letzter Sync:</span>
                  <span>{formatDE(status.last_sync_at)}</span>
                </>
              )}
              {status.default_business_location_name && (
                <>
                  <span style={{ color: colors.textMuted }}>Standard-Standort:</span>
                  <span style={{ fontWeight: 600 }}>{status.default_business_location_name}</span>
                </>
              )}
            </div>

            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              style={{
                padding: '8px 16px', borderRadius: 8, border: `1px solid ${colors.danger}`,
                background: 'transparent', cursor: disconnecting ? 'not-allowed' : 'pointer',
                fontSize: 12, fontWeight: 600, color: colors.danger,
                opacity: disconnecting ? 0.6 : 1,
              }}
            >
              {disconnecting ? 'Trenne...' : 'Verbindung trennen'}
            </button>
          </>
        )}
      </Card>

      {/* ── Section 2: Standorte ── */}
      {status?.connected && !errorCode && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: colors.navy, margin: 0 }}>
              Standorte
            </h2>
            <button
              onClick={handleLoadBusinesses}
              disabled={loadingBusinesses}
              style={btnOutline(colors.slate)}
            >
              <FaSync style={{ fontSize: 11 }} />
              {loadingBusinesses ? 'Lade...' : businesses.length > 0 ? 'Aktualisieren' : 'Standorte laden'}
            </button>
          </div>

          {businesses.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={labelStyle}>Business</label>
                  <select
                    value={selectedBusiness ?? ''}
                    onChange={e => {
                      const val = Number(e.target.value);
                      setSelectedBusiness(val || null);
                      setSelectedLocation(null);
                    }}
                    style={selectStyle}
                  >
                    <option value="">Bitte wählen...</option>
                    {businesses.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>

                {availableLocations.length > 0 && (
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={labelStyle}>Location</label>
                    <select
                      value={selectedLocation ?? ''}
                      onChange={e => setSelectedLocation(Number(e.target.value) || null)}
                      style={selectStyle}
                    >
                      <option value="">Bitte wählen...</option>
                      {availableLocations.map(l => (
                        <option key={l.id} value={l.id}>{l.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedLocation && (
                  <button
                    onClick={handleSaveMapping}
                    disabled={savingMapping}
                    style={{
                      ...btnPrimary(colors.slate),
                      opacity: savingMapping ? 0.6 : 1,
                      cursor: savingMapping ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {savingMapping ? 'Speichere...' : 'Als Standard speichern'}
                  </button>
                )}
              </div>
            </div>
          )}

          {businesses.length === 0 && !loadingBusinesses && (
            <p style={{ fontSize: 13, color: colors.textMuted, margin: 0 }}>
              Klicke "Standorte laden" um Businesses und Locations aus Lightspeed abzurufen.
            </p>
          )}
        </Card>
      )}

      {/* ── Section 3: Import ── */}
      {status?.connected && !errorCode && hasMapping && (
        <Card>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: colors.navy, margin: '0 0 16px' }}>
            Umsatz-Import
          </h2>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <button
              onClick={() => handleImport(48)}
              disabled={importing}
              style={{
                ...btnOutline(LS_GREEN),
                opacity: importing ? 0.6 : 1,
                cursor: importing ? 'not-allowed' : 'pointer',
              }}
            >
              <FaFlask style={{ fontSize: 11 }} />
              {importing ? 'Importiere...' : 'Import testen (letzte 48h)'}
            </button>
            <button
              onClick={() => handleImport(168)}
              disabled={importing}
              style={{
                ...btnPrimary(LS_GREEN),
                opacity: importing ? 0.6 : 1,
                cursor: importing ? 'not-allowed' : 'pointer',
              }}
            >
              <FaDownload style={{ fontSize: 11 }} />
              {importing ? 'Importiere...' : 'Import starten (letzte 7 Tage)'}
            </button>
          </div>

          {importResult && (
            <div style={{
              padding: 16, borderRadius: 10, background: colors.bgMuted,
              border: `1px solid ${colors.borderLight}`, fontSize: 13,
            }}>
              <div style={{
                display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px',
                color: colors.textBody,
              }}>
                <span style={{ color: colors.textMuted }}>Transaktionen:</span>
                <span style={{ fontWeight: 700 }}>{importResult.sales_count}</span>

                <span style={{ color: colors.textMuted }}>Zeitraum:</span>
                <span>{formatDE(importResult.from)} – {formatDE(importResult.to)}</span>

                {importResult.total_gross != null && (
                  <>
                    <span style={{ color: colors.textMuted }}>Brutto gesamt:</span>
                    <span style={{ fontWeight: 600 }}>
                      {importResult.total_gross.toLocaleString('de-DE', { style: 'currency', currency: importResult.currency || 'EUR' })}
                    </span>
                  </>
                )}

                {importResult.errors && importResult.errors.length > 0 && (
                  <>
                    <span style={{ color: colors.danger }}>Fehler:</span>
                    <span style={{ color: colors.danger }}>{importResult.errors.join(', ')}</span>
                  </>
                )}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

/* ── Sub-components ── */

function StatusBadge({ status, connecting }: { status: LightspeedStatus | null; connecting: boolean }): React.JSX.Element {
  if (connecting) {
    return <span style={badge('#e0f7f1', '#065f46')}>Verbinde...</span>;
  }
  if (!status?.connected) {
    return <span style={badge(colors.bgMuted, colors.textMuted)}>Nicht verbunden</span>;
  }
  if (status.error_code) {
    const isCritical = status.error_code === 'reauth_required' || status.error_code === 'forbidden_scope_or_access';
    return <span style={badge(isCritical ? colors.dangerLight : colors.warningLight,
      isCritical ? '#991b1b' : '#92400e')}>Fehler</span>;
  }
  return <span style={badge(colors.successLight, '#166534')}>Verbunden</span>;
}

function Card({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{
      background: colors.bgCard, borderRadius: 16,
      border: `1px solid ${colors.border}`, boxShadow: shadows.card,
      padding: '20px 24px', marginBottom: 16,
    }}>
      {children}
    </div>
  );
}

/* ── Style helpers ── */

function badge(bg: string, color: string): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '4px 12px', borderRadius: 9999, fontSize: 12, fontWeight: 600,
    background: bg, color,
  };
}

function btnPrimary(bg: string): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '9px 18px', borderRadius: 8, border: 'none',
    background: bg, color: '#fff', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  };
}

function btnOutline(color: string): React.CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 16px', borderRadius: 8, border: `1px solid ${color}`,
    background: 'transparent', color, fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  };
}

function btnSmall(bg: string): React.CSSProperties {
  return {
    padding: '5px 12px', borderRadius: 6, border: 'none',
    background: bg, color: '#fff', fontSize: 12, fontWeight: 600,
    cursor: 'pointer',
  };
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: colors.textMuted, marginBottom: 4,
};

const selectStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  border: `1px solid ${colors.border}`, fontSize: 13,
  outline: 'none', background: colors.bgCard,
};
